/**
 * useModals.ts
 * Hook pour gérer l'état de tous les modals de l'application
 */
import { useState, useEffect, useCallback } from 'react';
import { useAppStore } from '../services/StateService';

export interface ModalsState {
  showAssetsLibrary: boolean;
  showTemplateGallery: boolean;
  showOnboarding: boolean;
  showHelpModal: boolean;
  showUserGuide: boolean;
  showTutorial: boolean;
  showKeyboardShortcuts: boolean;
  helpModalContent: { title?: string; message?: string } | null;
}

export interface ModalsActions {
  setShowAssetsLibrary: (show: boolean) => void;
  setShowTemplateGallery: (show: boolean) => void;
  setShowOnboarding: (show: boolean) => void;
  setShowHelpModal: (show: boolean) => void;
  setShowUserGuide: (show: boolean) => void;
  setShowTutorial: (show: boolean) => void;
  setShowKeyboardShortcuts: (show: boolean) => void;
  setHelpModalContent: (content: { title?: string; message?: string } | null) => void;
  closeAllModals: () => void;
}

/**
 * Hook pour gérer tous les modals de l'application
 */
export function useModals(): ModalsState & ModalsActions {
  // Modals depuis le store
  const showAssetsLibrary = useAppStore(state => state.ui.showAssetsLibrary);
  const showTemplateGallery = useAppStore(state => state.ui.showTemplateGallery);
  const setShowAssetsLibrary = useAppStore(state => state.setShowAssetsLibrary);
  const setShowTemplateGallery = useAppStore(state => state.setShowTemplateGallery);

  // Modals locaux
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showUserGuide, setShowUserGuide] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);
  const [helpModalContent, setHelpModalContent] = useState<{
    title?: string;
    message?: string;
  } | null>(null);

  // Vérifier si c'est le premier démarrage
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('snapbooth-onboarding-seen');
    const hasSeenTutorial = localStorage.getItem('snapbooth-tutorial-seen');

    if (!hasSeenOnboarding) {
      setShowOnboarding(true);
    }

    if (!hasSeenTutorial && hasSeenOnboarding) {
      setTimeout(() => setShowTutorial(true), 500);
    }
  }, []);

  // Écouter les événements pour ouvrir HelpModal
  useEffect(() => {
    const handleShowHelp = (event: CustomEvent<{ title?: string; message?: string }>) => {
      setHelpModalContent({
        title: event.detail.title,
        message: event.detail.message,
      });
      setShowHelpModal(true);
    };

    const handleShowUserGuide = () => {
      setShowUserGuide(true);
    };

    const handleCloseUserGuide = () => {
      setShowUserGuide(false);
    };

    // Câblage des commandes ⌘K (raccourcis & tour guidé).
    const handleShowShortcuts = () => setShowKeyboardShortcuts(true);
    const handleShowOnboarding = () => setShowOnboarding(true);

    const showHelpEvent = 'show-help' as keyof WindowEventMap;
    const showUserGuideEvent = 'show-user-guide' as keyof WindowEventMap;
    const closeUserGuideEvent = 'close-user-guide' as keyof WindowEventMap;
    const showShortcutsEvent = 'show-shortcuts' as keyof WindowEventMap;
    const showOnboardingEvent = 'show-onboarding' as keyof WindowEventMap;

    window.addEventListener(showHelpEvent, handleShowHelp as EventListener);
    window.addEventListener(showUserGuideEvent, handleShowUserGuide as EventListener);
    window.addEventListener(closeUserGuideEvent, handleCloseUserGuide as EventListener);
    window.addEventListener(showShortcutsEvent, handleShowShortcuts as EventListener);
    window.addEventListener(showOnboardingEvent, handleShowOnboarding as EventListener);

    return () => {
      window.removeEventListener(showHelpEvent, handleShowHelp as EventListener);
      window.removeEventListener(showUserGuideEvent, handleShowUserGuide as EventListener);
      window.removeEventListener(closeUserGuideEvent, handleCloseUserGuide as EventListener);
      window.removeEventListener(showShortcutsEvent, handleShowShortcuts as EventListener);
      window.removeEventListener(showOnboardingEvent, handleShowOnboarding as EventListener);
    };
  }, []);

  // Écouter F1 pour ouvrir les raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      if (e.key === 'F1' || (e.shiftKey && e.key === '?')) {
        e.preventDefault();
        setShowKeyboardShortcuts(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const closeAllModals = useCallback(() => {
    setShowAssetsLibrary(false);
    setShowTemplateGallery(false);
    setShowOnboarding(false);
    setShowHelpModal(false);
    setShowUserGuide(false);
    setShowTutorial(false);
    setShowKeyboardShortcuts(false);
    setHelpModalContent(null);
  }, [setShowAssetsLibrary, setShowTemplateGallery]);

  return {
    showAssetsLibrary,
    showTemplateGallery,
    showOnboarding,
    showHelpModal,
    showUserGuide,
    showTutorial,
    showKeyboardShortcuts,
    helpModalContent,
    setShowAssetsLibrary,
    setShowTemplateGallery,
    setShowOnboarding,
    setShowHelpModal,
    setShowUserGuide,
    setShowTutorial,
    setShowKeyboardShortcuts,
    setHelpModalContent,
    closeAllModals,
  };
}
